import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Typography} from 'views/components';
import {Translate, useNavigate, useQuery} from 'views/module';
import featureFlags from 'utils/feature_flags/feature_flags';

import CommunicationPanel from './communication/CommunicationPanel';
import CaseInbox from './list/CaseInbox';
import CaseView from './view/CaseView';

import {getParticipantKind, getParticipantResourceRn, getParticipantRns} from './communication/communicationUtils';
import backend from 'utils/backend';
import moment from 'moment-timezone';

const caseStatusFilterValues = ['OPEN', 'PENDING', 'COMPLETE'];
const caseSort = {sortKey: 'updatedAt', sortDirection: 'DESC'};
const caseDateRanges = [
  {key: 'TODAY', days: 1},
  {key: 'LAST_3_DAYS', days: 3},
  {key: 'LAST_7_DAYS', days: 7},
  {key: 'LAST_28_DAYS', days: 28},
];
const caseTypeGroups = [
  {
    key: 'ALL',
  },
  {
    key: 'SALES',
    teamName: 'sales',
  },
  {
    key: 'RECRUITING',
    teamName: 'recruiting',
  },
  {
    key: 'OPERATIONS',
    teamName: 'operations',
  },
];
// Each mark option carries the field the case-query endpoint filters on, so the
// count reflects the marks in the range rather than the marks among whichever
// pages happened to be loaded.
//
// The field varies by option. markingStatus is a bucket — SKIPPED_INTERVENTION
// and UNCERTAIN both collapse into FLAGGED — so the two options that have to
// tell those apart filter on markingGrade, which keeps the teacher's actual
// verdict. Flagged therefore means teacher uncertainty ONLY; a case the teacher
// skipped because a human stepped in is its own option, matching how the daily
// marking card has always counted them.
//
// UNREVIEWED carries neither, because "no mark at all" is not a stored value and
// an equality filter cannot express it. That one option stays a client-side
// pass, and it is pinned to the case date — an unreviewed case has no marked day
// to filter on.
const markingFilters = [
  {key: 'ALL'},
  {key: 'MARKED_BAD', markingStatus: 'MARKED_BAD'},
  {key: 'FLAGGED', markingGrade: 'UNCERTAIN'},
  {key: 'HUMAN_INTERVENTION', markingGrade: 'SKIPPED_INTERVENTION'},
  {key: 'MARKED_GOOD', markingStatus: 'MARKED_GOOD'},
  {key: 'UNREVIEWED'},
];
// Which date the range applies to. A sweep marks month-old cases, so the two
// answers differ by weeks, and only MARKED_DATE reconciles with a daily rollup
// card — the card counts the marks made that day, whenever the cases happened.
const caseDateBases = [
  {key: 'CASE_DATE'},
  {key: 'MARKED_DATE'},
];
// metricDate is a Toronto civil day, so the day list is built in Toronto and
// not in whatever zone the reader's browser is in.
const markingTimezone = 'America/Toronto';
const emptyTeamCaseTypeValue = '__NO_DOMAIN_GUIDE_CASE_TYPE_MATCH__';

// A linked-to filter is only honoured when the inbox actually offers it as an
// option: an unknown key would leave the reader looking at a list filtered by
// something no dropdown can show or clear.
const initialFilterKey = (filterOptions, paramValue, defaultKey) => {
  return filterOptions.some((filterOption) => filterOption.key === paramValue) ? paramValue : defaultKey;
};

// The query filter an option stands for, as the {key, value} pair the endpoint
// takes — the key varies because the options do not all filter on the same
// field. Null for the options that are not a query filter at all (ALL,
// UNREVIEWED), which is also what gates the marked-date basis.
const getMarkingQueryFilter = (markingFilterKey) => {
  const markingFilter = markingFilters.find((option) => option.key === markingFilterKey);

  if (markingFilter?.markingGrade) {
    return {
      key: 'markingGrade',
      value: markingFilter.markingGrade,
    };
  }

  if (markingFilter?.markingStatus) {
    return {
      key: 'markingStatus',
      value: markingFilter.markingStatus,
    };
  }

  return null;
};

// The only mark filtering left over a loaded page: every other option is a
// real query filter now (see markingFilters).
const matchesUnreviewedFilter = (caseData, markingFilterKey) => {
  return markingFilterKey !== 'UNREVIEWED' || !caseData.markingStatus;
};

// The selected case is fetched by RN rather than through the filtered query, so
// it is the one case that still has to be checked against the whole filter
// before being pinned to the top of the list. It is compared against whichever
// field the option names rather than against the option key: HUMAN_INTERVENTION
// is not a markingStatus value, so a key comparison would judge every
// intervention case non-matching and silently drop it from the top of the list.
//
// The detail fetch is /case/{caseRn}, which carries no marking field at all
// today, so under any mark filter this already answers no and the selected case
// is only listed when the filtered query returns it too. Written against the
// field rather than the key so it becomes correct — rather than differently
// wrong — if that endpoint ever projects the mark.
const matchesMarkingFilter = (caseData, markingFilterKey) => {
  if (markingFilterKey === 'ALL') {
    return true;
  }

  if (markingFilterKey === 'UNREVIEWED') {
    return !caseData.markingStatus;
  }

  const markingQueryFilter = getMarkingQueryFilter(markingFilterKey);

  return Boolean(markingQueryFilter) && caseData[markingQueryFilter.key] === markingQueryFilter.value;
};

// A marked-date range is an enumerated day list rather than a bound, because
// markingMetricDate is indexed as a literal day string — which is exactly what
// makes it match the rollup's own key with no timezone arithmetic in between.
const buildMarkedMetricDates = (days) => {
  return Array.from({length: days}, (unusedValue, dayOffset) => {
    return moment().tz(markingTimezone).subtract(dayOffset, 'days').format('YYYY-MM-DD');
  });
};

const formatCaseTypeForQuery = (caseType) => {
  if (!caseType) {
    return '';
  }

  return `${caseType}`.trim().replace(/-/g, '_').toUpperCase();
};

const getCaseUpdatedAt = (caseData) => {
  const updatedAtTime = new Date(caseData.updatedAt).getTime();

  return Number.isNaN(updatedAtTime) ? 0 : updatedAtTime;
};

const getCasePriority = (caseData) => {
  if (caseData.priority === null || caseData.priority === undefined || caseData.priority === '') {
    return Number.MAX_SAFE_INTEGER;
  }

  const priority = Number(caseData.priority);

  if (Number.isFinite(priority)) {
    return priority;
  }

  return Number.MAX_SAFE_INTEGER;
};

const sortCasesByPriority = (caseItems) => {
  return [...caseItems].sort((firstCase, secondCase) => {
    const priorityDifference = getCasePriority(firstCase) - getCasePriority(secondCase);

    if (priorityDifference !== 0) {
      return priorityDifference;
    }

    return getCaseUpdatedAt(secondCase) - getCaseUpdatedAt(firstCase);
  });
};

const mergeCasesByRn = (currentCases, nextCases) => {
  const mergedCases = [...currentCases];
  const caseIndexByRn = {};
  mergedCases.forEach((caseData, index) => {
    caseIndexByRn[caseData.caseRn] = index;
  });

  nextCases.forEach((caseData) => {
    if (!caseData?.caseRn) return;

    const existingIndex = caseIndexByRn[caseData.caseRn];

    if (existingIndex === undefined) {
      caseIndexByRn[caseData.caseRn] = mergedCases.length;
      mergedCases.push(caseData);
      return;
    }

    mergedCases[existingIndex] = {
      ...mergedCases[existingIndex],
      ...caseData,
    };
  });

  return mergedCases;
};

const CaseScene = (props) => {
  const {
    selectedCaseRn,
    statusProgressWrap: progressWrap,
  } = props;
  const navigate = useNavigate();
  // Query params seed the filters ONCE, so a Slack card can link straight to
  // the slice it is reporting on. They are never written back: the dropdowns
  // stay the source of truth for the rest of the session, and selecting a case
  // drops the search string, so a stale param can never resurrect a filter the
  // reader has since changed.
  const query = useQuery();
  const [cases, setCases] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState();
  const [dateRangeKey, setDateRangeKey] = React.useState(() => {
    return initialFilterKey(caseDateRanges, query.dateRange, 'TODAY');
  });
  const [caseTypeGroupKey, setCaseTypeGroupKey] = React.useState('ALL');
  const [markingFilterKey, setMarkingFilterKey] = React.useState(() => {
    // The mark dropdown only renders behind the marking-reads flag, so without
    // it a linked mark filter would be unclearable. dateRange has no such gate.
    return featureFlags.oversightMarkingReadsEnabled ?
      initialFilterKey(markingFilters, query.mark, 'ALL') : 'ALL';
  });
  const [dateBasisKey, setDateBasisKey] = React.useState(() => {
    // A linked mark filter comes from a rollup card, so it opens on the basis
    // whose count matches that card. Picked from the same seeded value the mark
    // filter starts on rather than in an effect, so the first load already
    // queries the right axis instead of loading twice.
    return featureFlags.oversightMarkingReadsEnabled &&
      getMarkingQueryFilter(initialFilterKey(markingFilters, query.mark, 'ALL')) ?
      'MARKED_DATE' : 'CASE_DATE';
  });
  const [pendingOnly, setPendingOnly] = React.useState(false);
  const [selectedCaseDetail, setSelectedCaseDetail] = React.useState(null);
  const [participantDetails, setParticipantDetails] = React.useState({});
  const [casesLoadFailed, setCasesLoadFailed] = React.useState(false);
  const [stage, setStage] = React.useState('FILL');
  const requestIdRef = React.useRef(0);
  const participantDetailsLoadingRef = React.useRef(new Set());

  const isLoading = stage === 'LOADING';
  // Memoised because it is a fresh object every render: buildCaseFilters depends
  // on it, and an identity that changed on every render would reload the list
  // on every render.
  const markingQueryFilter = React.useMemo(() => getMarkingQueryFilter(markingFilterKey), [markingFilterKey]);
  // UNREVIEWED cases have no marked day, so a marked-date query for them would
  // always come back empty however the dropdown is set.
  const effectiveDateBasisKey = markingQueryFilter ? dateBasisKey : 'CASE_DATE';
  const selectedDateRangeDays = React.useMemo(() => {
    const selectedDateRange = caseDateRanges.find((dateRange) => dateRange.key === dateRangeKey) || caseDateRanges[2];

    return selectedDateRange.days;
  }, [dateRangeKey]);
  const selectedDateRangeStart = React.useMemo(() => {
    return moment().subtract(selectedDateRangeDays - 1, 'days').startOf('day').toISOString();
  }, [selectedDateRangeDays]);
  const selectedMarkedMetricDates = React.useMemo(() => {
    return buildMarkedMetricDates(selectedDateRangeDays);
  }, [selectedDateRangeDays]);
  const buildCaseFilters = React.useCallback(async () => {
    const selectedCaseTypeGroup = caseTypeGroups.find((caseTypeGroup) => caseTypeGroup.key === caseTypeGroupKey);
    const filters = [{
      key: 'status',
      value: pendingOnly ? ['PENDING'] : caseStatusFilterValues,
    }];

    if (markingQueryFilter) {
      filters.push(markingQueryFilter);
    }

    if (effectiveDateBasisKey === 'MARKED_DATE') {
      filters.push({
        key: 'markingMetricDate',
        value: selectedMarkedMetricDates,
      });
    }

    if (selectedCaseTypeGroup?.teamName) {
      const domainGuideResponse = await backend.domainGuide.listDomainGuides({teamName: selectedCaseTypeGroup.teamName});
      const domainGuideList = Array.isArray(domainGuideResponse) ? domainGuideResponse : domainGuideResponse?.list;
      const caseTypes = [...new Set((domainGuideList || [])
          .map((domainGuideData) => formatCaseTypeForQuery(domainGuideData.name))
          .filter(Boolean))];

      filters.push({
        key: 'caseType',
        value: caseTypes.length ? caseTypes : [emptyTeamCaseTypeValue],
      });
    }

    return filters;
  }, [
    caseTypeGroupKey,
    effectiveDateBasisKey,
    markingQueryFilter,
    pendingOnly,
    selectedMarkedMetricDates,
  ]);
  const sortedCases = React.useMemo(() => {
    return sortCasesByPriority(cases.filter((caseData) => matchesUnreviewedFilter(caseData, markingFilterKey)));
  }, [cases, markingFilterKey]);
  const visibleCases = React.useMemo(() => {
    if (
      !selectedCaseDetail?.caseRn ||
      selectedCaseDetail.caseRn !== selectedCaseRn ||
      (pendingOnly && selectedCaseDetail.status !== 'PENDING') ||
      !matchesMarkingFilter(selectedCaseDetail, markingFilterKey) ||
      cases.some((caseData) => caseData.caseRn === selectedCaseDetail.caseRn)
    ) {
      return sortedCases;
    }

    return [
      selectedCaseDetail,
      ...sortedCases,
    ];
  }, [cases, markingFilterKey, pendingOnly, selectedCaseDetail, selectedCaseRn, sortedCases]);
  const selectedCaseSummary = React.useMemo(() => {
    return visibleCases.find((caseData) => caseData.caseRn === selectedCaseRn) || null;
  }, [selectedCaseRn, visibleCases]);
  const selectedCaseData = React.useMemo(() => {
    if (selectedCaseDetail?.caseRn === selectedCaseRn) {
      return selectedCaseDetail;
    }

    return selectedCaseSummary;
  }, [selectedCaseDetail, selectedCaseRn, selectedCaseSummary]);
  const selectedParticipantRns = React.useMemo(() => {
    return [...new Set(getParticipantRns(selectedCaseData))];
  }, [selectedCaseData]);
  const selectedParticipantResourceRns = React.useMemo(() => {
    return [...new Set(selectedParticipantRns.map((participantRn) => getParticipantResourceRn(participantRn)).filter(Boolean))];
  }, [selectedParticipantRns]);
  const onSelectedCaseDataChange = React.useCallback((nextCaseData) => {
    setSelectedCaseDetail(nextCaseData);

    if (!nextCaseData?.caseRn) return;

    setCases((currentCases) => {
      if (!currentCases.some((caseData) => caseData.caseRn === nextCaseData.caseRn)) {
        return currentCases;
      }

      return currentCases.map((caseData) => (
        caseData.caseRn === nextCaseData.caseRn ?
          {
            ...caseData,
            ...nextCaseData,
          } :
          caseData
      ));
    });
  }, []);
  const selectCaseRn = React.useCallback((caseRn, options={}) => {
    if (caseRn) {
      navigate(`/case/${encodeURIComponent(caseRn)}`, {replace: Boolean(options.replace)});
      return;
    }

    navigate('/cases', {replace: Boolean(options.replace)});
  }, [navigate]);

  const loadCases = React.useCallback(async (
      startKey,
      shouldReset = false,
      requestId = requestIdRef.current,
      showProgress = true,
  ) => {
    await progressWrap(async () => {
      try {
        const filters = await buildCaseFilters();

        if (requestId !== requestIdRef.current) return;

        // On the marked-date basis the day list is the whole bound, so the
        // undated endpoint is the right one: the from-date endpoint would layer
        // a createdAt range on top and narrow the result back to cases that
        // happened in the window rather than were marked in it.
        const caseResponse = effectiveDateBasisKey === 'MARKED_DATE' ?
          await backend.case.queryCase(filters, caseSort, startKey) :
          await backend.case.queryCaseFromDate(selectedDateRangeStart, filters, caseSort, startKey);

        if (requestId !== requestIdRef.current) return;

        setCases((currentCases) => {
          const nextCases = caseResponse?.list || [];
          return shouldReset ? mergeCasesByRn([], nextCases) : mergeCasesByRn(currentCases, nextCases);
        });
        setLastEvaluatedKey(caseResponse?.lastEvaluatedKey);
        setCasesLoadFailed(false);
      } catch {
        if (requestId !== requestIdRef.current) return;

        if (shouldReset) {
          setCases([]);
          setLastEvaluatedKey();
        }
        setCasesLoadFailed(true);
      }
    }, {setStage, showProgress});
  }, [buildCaseFilters, effectiveDateBasisKey, progressWrap, selectedDateRangeStart]);

  React.useEffect(() => {
    if (selectedCaseRn || sortedCases.length === 0) return;

    selectCaseRn(visibleCases[0].caseRn, {replace: true});
  }, [selectCaseRn, selectedCaseRn, sortedCases, visibleCases]);

  React.useEffect(() => {
    setSelectedCaseDetail(null);
  }, [selectedCaseRn]);

  React.useEffect(() => {
    const participantResourceRnsToLoad = selectedParticipantResourceRns.filter((participantResourceRn) => {
      return !participantDetails[participantResourceRn] && !participantDetailsLoadingRef.current.has(participantResourceRn);
    });

    if (participantResourceRnsToLoad.length === 0) return;

    participantResourceRnsToLoad.forEach((participantResourceRn) => {
      participantDetailsLoadingRef.current.add(participantResourceRn);
    });

    const loadParticipantDetails = async () => {
      const nextParticipantDetails = await Promise.all(participantResourceRnsToLoad.map(async (participantResourceRn) => {
        const participantKind = getParticipantKind(participantResourceRn);

        if (!['CLIENT', 'CAREGIVER'].includes(participantKind)) {
          return {
            participantRn: participantResourceRn,
            detail: null,
            loadFailed: false,
          };
        }

        try {
          const output = participantKind === 'CLIENT' ?
            await backend.client.getClient(participantResourceRn) :
            await backend.caregiver.getCaregiver(participantResourceRn);

          return {
            participantRn: participantResourceRn,
            detail: participantKind === 'CLIENT' ? output?.clientDetail : output?.caregiverDetail,
            loadFailed: false,
          };
        } catch {
          return {
            participantRn: participantResourceRn,
            detail: null,
            loadFailed: true,
          };
        }
      }));

      participantResourceRnsToLoad.forEach((participantResourceRn) => {
        participantDetailsLoadingRef.current.delete(participantResourceRn);
      });

      setParticipantDetails((currentParticipantDetails) => {
        return nextParticipantDetails.reduce((result, participantDetail) => {
          return {
            ...result,
            [participantDetail.participantRn]: {
              detail: participantDetail.detail,
              loadFailed: participantDetail.loadFailed,
            },
          };
        }, currentParticipantDetails);
      });
    };

    loadParticipantDetails();
  }, [participantDetails, selectedParticipantResourceRns]);

  React.useEffect(() => {
    requestIdRef.current += 1;
    setCases([]);
    setLastEvaluatedKey();
    setCasesLoadFailed(false);
    loadCases(null, true, requestIdRef.current);

    return () => {
      requestIdRef.current += 1;
    };
  }, [loadCases]);

  const loadMoreCases = () => {
    if (!lastEvaluatedKey || isLoading) return;
    loadCases(lastEvaluatedKey, false, requestIdRef.current, false);
  };
  // Choosing a mark also chooses the axis whose count matches a rollup card;
  // the reader can still switch back to compare. Cleared together, so clearing
  // the mark never leaves a marked-date query behind with nothing to date.
  const changeMarkingFilterKey = React.useCallback((nextMarkingFilterKey) => {
    setMarkingFilterKey(nextMarkingFilterKey);
    setDateBasisKey(getMarkingQueryFilter(nextMarkingFilterKey) ? 'MARKED_DATE' : 'CASE_DATE');
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: {
          xs: 'column',
          md: 'row',
        },
        width: '100%',
        height: {
          md: '97vh',
        },
        minHeight: {
          md: '97vh',
        },
        overflow: {
          md: 'hidden',
        },
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid #ECECF1',
      }}
    >
      <CaseInbox
        casesLoadFailed={casesLoadFailed}
        caseDateBases={caseDateBases}
        caseDateRanges={caseDateRanges}
        caseTypeGroupKey={caseTypeGroupKey}
        caseTypeGroups={caseTypeGroups}
        dateBasisEnabled={Boolean(markingQueryFilter)}
        dateBasisKey={effectiveDateBasisKey}
        dateRangeKey={dateRangeKey}
        markingFilterKey={markingFilterKey}
        markingFilters={markingFilters}
        isLoading={isLoading}
        lastEvaluatedKey={lastEvaluatedKey}
        loadMoreCases={loadMoreCases}
        pendingOnly={pendingOnly}
        selectedCaseRn={selectedCaseRn}
        setCaseTypeGroupKey={setCaseTypeGroupKey}
        setDateBasisKey={setDateBasisKey}
        setDateRangeKey={setDateRangeKey}
        setMarkingFilterKey={changeMarkingFilterKey}
        setPendingOnly={setPendingOnly}
        selectCaseRn={selectCaseRn}
        visibleCases={visibleCases}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: {
            xs: '480px',
            md: 0,
          },
        }}
      >
        {
          selectedCaseRn ? (
            <CaseView
              caseRn={selectedCaseRn}
              isEmbedded
              onCaseDataChange={onSelectedCaseDataChange}
              participantDetails={participantDetails}
            />
          ) : (
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: '#6D7280',
                px: {
                  xs: 2,
                  md: 2,
                  lg: 3,
                },
                py: 3,
              }}
            >
              <Translate expression='case.List.NoCaseSelected' />
            </Typography>
          )
        }
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          width: {
            xs: '100%',
            md: '364px',
            lg: '364px',
          },
          borderLeft: {
            md: '1px solid #ECECF1',
          },
          minHeight: {
            xs: '320px',
            md: 0,
          },
        }}
      >
        <CommunicationPanel
          caseData={selectedCaseData}
          participantDetails={participantDetails}
        />
      </Box>
    </Box>
  );
};

CaseScene.propTypes = {
  selectedCaseRn: PropTypes.string,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(CaseScene));
